import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import rootReducer from '../reducers';
import { installSessionInterceptor } from '../util/axiosSessionInterceptor';
import { loadCloudSession } from '../backend/cloudSession';
import { PRODUCT_MODE, getProductMode } from '../backend/productMode';

// Validate the cloud-session copy before redux-persist reads its matching grid.
// loadCloudSession clears both legacy caches when any camera row lacks fPtz.
if (getProductMode() === PRODUCT_MODE.CLOUD) loadCloudSession();

/**
 * Redux Persist Configuration
 *
 * We persist most reducers so that page refresh restores the user's full state
 * (cameras, views, playback position, server info, etc.) without re-login.
 *
 * On refresh, App.js validates the session with auth.checkExists:
 * - Valid session → user stays on their current route with full state
 * - Invalid session → state is purged, user goes to login
 *
 * Security note: the auth reducer keeps `username` AND `password` in state
 * for the entire session — they're needed (a) to re-establish the session
 * after a jump and (b) for the eview cross-NVR stream URL fallback in
 * util/getServerInfo.js (mpengine's webcam.c only honors the session token
 * for the NVR that issued it; child NVRs need ?user=&pass= on stream URLs,
 * mirroring Java dview LiveStream.java:97-117). The auth transform below
 * only strips `loading` and `loginResult`; credentials persist to
 * localStorage as a consequence of needing them at the URL layer.
 *
 * Excluded from persistence:
 * - utility: Platform/browser info (re-detected on every load, trivial)
 */

// Strip transient fields from auth before persisting (keep credentials —
// username/password are needed in-session for jump-system re-login and
// cross-NVR stream URL auth fallback).
const authTransform = createTransform(
  // inbound: what gets written to storage
  (inboundState) => {
    const { loading, loginResult, ...safe } = inboundState;
    return safe;
  },
  // outbound: what gets read back from storage
  (outboundState) => {
    return {
      ...outboundState,
      loading: false,
      loginResult: ''
    };
  },
  { whitelist: ['auth'] }
);

// Strip the MCPlayback SELECTION for persistence but drop the heavy/transient bits: the fetched
// trackEvents (large; re-fetched on mount via the rehydrated backend) and the load flags. Persisting
// the selection (conf/slots/selectedTimestamp/window/filters/playheadOffset) is what makes a page
// refresh land back on the GRID at the same window+position instead of the entry form.
const mcplaybackTransform = createTransform(
  (inboundState) => {
    const { trackEvents, trackEventsWindow, eventsLoading, eventsError, ...safe } = inboundState;
    return safe;
  },
  (outboundState) => ({
    ...outboundState,
    trackEvents: {},        // re-fetched on mount (MCPlaybackView) — never restore stale events
    trackEventsWindow: '',
    eventsLoading: false,
    eventsError: false,
  }),
  { whitelist: ['mcplayback'] }
);

// Strip the plaintext password from the `user` slice before persisting. Unlike auth.password
// (deliberately kept for jump + eView stream-URL auth, see note above), user.sPassword is an
// undocumented, unused second at-rest copy (no live consumer reads state.user.sPassword) — keep it
// out of persist:root.
export const userTransform = createTransform(
  (inboundState) => {
    const { sPassword, ...safe } = inboundState;
    return safe;
  },
  (outboundState) => outboundState,
  { whitelist: ['user'] }
);

// The access notice ("X is no longer available to your account") is about the moment access changed. Persisted
// with the rest of the navigation slice, a notice not yet dismissed came back after a reload even when access had
// been restored meanwhile (#86bc7maf2 headed pass). Never write it, and drop one written before this fix.
export const navigationTransform = createTransform(
  (inboundState) => {
    const { accessNotice, ...safe } = inboundState;
    return safe;
  },
  (outboundState) => ({ ...outboundState, accessNotice: '' }),
  { whitelist: ['navigation'] }
);

export const persistConfig = {
  key: 'root',
  storage: storage,
  whitelist: [
    'auth',       // Session token, isLoggedIn, username + password (kept in-session for jump-system + eview stream URL auth)
    'server',     // Server info, cameras, eView state, serverSessions
    'user',       // User ID, rights, cameras
    'config',     // Grid layout preferences
    'camera',     // Camera button state
    'view',       // User view selections
    'video',      // Single cam view, fullscreen state
    'playback',   // Playback camera, date, clips, rate
    'navigation', // Current screen, serverUrl
    'mcplayback'  // MCPlayback SELECTION (cams/window/filters/playhead) so refresh restores the grid, not the form (events re-fetched)
  ],
  // userTransform MUST stay in this list. It was defined but never registered (#86bb7phhh), so the
  // plaintext-password cleanup it implements silently did nothing — user.sPassword kept landing in
  // persist:root — and the unused binding also failed `CI=true npm run build` on no-unused-vars.
  transforms: [authTransform, mcplaybackTransform, userTransform, navigationTransform]
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = createStore(persistedReducer, applyMiddleware(thunk));
export const persistor = persistStore(store);

// Dev-only debug handle (excluded from production builds) — lets visual QA populate MCPlayback
// slots/clips before the full camera-drawer / time-nav UI exists. Mirrors the app's existing
// window.__dlTest-style dev hooks. No effect in prod.
if (process.env.NODE_ENV !== 'production') {
  window.__mcpbStore = store;
}

// Install axios interceptor for centralized session-expiry detection.
// Every JSON-RPC response is checked for "noauth" — triggers EXPIRE_SESSION
// on any screen (live, playback, setup, export) without per-component wiring.
installSessionInterceptor(store);
